import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { AdminActions } from "src/redux/Admin/actions";
import { AdminSelectors } from "src/redux/Admin/selectors";
import Button from "src/components/common/Button";
import UserRow, { CPTY_ROLES } from "./UserRow";
import PendingCreateRow from "./PendingCreateRow";
import useStagedUserChanges from "./useStagedUserChanges";
import { validateNewUserEmail, parseMailDomains } from "./validateNewUser";
import generatePassword from "./generatePassword";
import styles from "./styles.module.scss";

const DEFAULT_RESET_SUBJECT = "Please reset your TruWeb password";

const DEFAULT_RESET_BODY = `A TruWeb administrator has reset your password. Please use the link below to set a new password.

{{link}}

If you have any questions, please contact ops@kslnetwork.com.

---

TruWeb 관리자가 귀하의 비밀번호를 재설정했습니다. 아래 링크를 사용하여 새 비밀번호를 설정해 주십시오.

{{link}}

문의사항이 있으시면 ops@kslnetwork.com 로 연락 주시기 바랍니다.`;

/**
 * Dedicated per-counterparty user management page (route /users/:cptyCode),
 * reached from "Manage Users" on the Users tab. Mirrors how a counterparty's
 * "Manage" opens CptyDetail: a back button to the list and the standard staged
 * confirm/discard bar with the unsaved-changes nav guard.
 */
const CptyUserManagement = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { cptyCode } = useParams();

    const [cptyUsers, setCptyUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [resetUser, setResetUser] = useState(null);
    const [resetSubject, setResetSubject] = useState(DEFAULT_RESET_SUBJECT);
    const [resetBody, setResetBody] = useState(DEFAULT_RESET_BODY);
    const [resetTtlValue, setResetTtlValue] = useState(7);
    const [resetTtlUnit, setResetTtlUnit] = useState("days");
    const [resetError, setResetError] = useState(null);
    const [resetBusy, setResetBusy] = useState(false);
    const [resetSentTo, setResetSentTo] = useState(null);

    const [totpUser, setTotpUser] = useState(null);
    const [totpError, setTotpError] = useState(null);
    const [totpBusy, setTotpBusy] = useState(false);
    const [totpClearedFor, setTotpClearedFor] = useState(null);

    const confirmResetTotp = async () => {
        setTotpBusy(true);
        setTotpError(null);
        try {
            await dispatch(AdminActions.ResetUserTotp(totpUser.realm, totpUser.id, "lost authenticator"));
            setTotpClearedFor(totpUser.email);
            setTotpUser(null);
            await loadCptyUsers();
        } catch (e) {
            setTotpError(e.message);
        } finally {
            setTotpBusy(false);
        }
    };

    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState(null);
    const [createError, setCreateError] = useState(null);

    const blankCreateForm = () => ({
        email: "",
        firstName: "",
        lastName: "",
        password: generatePassword(),
        role: CPTY_ROLES[0],
        // The operator vouches for an address they typed themselves. Left
        // unverified, the realm's verifyEmail would block the first login until
        // the holder acted on a mail nobody asked them to expect.
        emailVerified: true,
    });

    const toggleCreate = () => {
        setCreateError(null);
        if (showCreate) {
            setShowCreate(false);
            return;
        }
        setCreateForm(blankCreateForm());
        setShowCreate(true);
    };

    // Creating a user is a change to the system like any other on this page, so
    // it is staged into the same confirm bar rather than written on its own. The
    // account appears at the top of the table until the batch is confirmed.
    const submitCreate = () => {
        const emailError = validateNewUserEmail(createForm.email, mailDomains);
        if (emailError) {
            setCreateError(emailError);
            return;
        }
        if (!createForm.password.trim()) {
            setCreateError("Initial password is required");
            return;
        }
        if (
            cptyUsers.some(
                (u) => u.email.toLowerCase() === createForm.email.trim().toLowerCase(),
            ) ||
            pendingCreates.some(
                (c) => c.email.toLowerCase() === createForm.email.trim().toLowerCase(),
            )
        ) {
            setCreateError("A user with this address already exists");
            return;
        }
        setCreateError(null);
        onStageCreate({ ...createForm, email: createForm.email.trim(), cptyCode });
        // The panel stays open on a fresh form, since users tend to be added a
        // few at a time and the batch is confirmed once at the end. Cancel
        // closes it.
        setCreateForm(blankCreateForm());
    };

    const openReset = (user) => {
        setResetSentTo(null);
        setResetError(null);
        setResetSubject(DEFAULT_RESET_SUBJECT);
        setResetBody(DEFAULT_RESET_BODY);
        setResetTtlValue(7);
        setResetTtlUnit("days");
        setResetUser(user);
    };

    const sendReset = async () => {
        setResetBusy(true);
        setResetError(null);
        const unitSeconds = { minutes: 60, hours: 3600, days: 86400 }[resetTtlUnit];
        const ttlSeconds = resetTtlValue * unitSeconds;
        try {
            await dispatch(AdminActions.SendPasswordReset(
                cptyCode, resetUser.email, resetSubject, resetBody, ttlSeconds));
            setResetSentTo(resetUser.email);
            setResetUser(null);
        } catch (e) {
            setResetError(e.message);
        } finally {
            setResetBusy(false);
        }
    };

    const loadCptyUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            setCptyUsers(await dispatch(AdminActions.FetchCptyUsers(cptyCode)));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadCptyUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cptyCode]);

    const cpties = useSelector(AdminSelectors.Cpties);

    useEffect(() => {
        if (!cpties || cpties.length === 0) dispatch(AdminActions.FetchCpties());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The counterparty's permitted email domains, which a new address has to sit
    // inside. Undefined until the cpty list arrives.
    const mailDomains = useMemo(() => {
        const cpty = (cpties || []).find((c) => c.cptyCode === cptyCode);
        return cpty ? cpty.mailDomains || "" : undefined;
    }, [cpties, cptyCode]);

    const allowedDomains = useMemo(() => parseMailDomains(mailDomains), [mailDomains]);

    const {
        pendingRoles,
        pendingActions,
        pendingCreates,
        onStageCreate,
        onUnstageCreate,
        onToggleEnabled,
        onDelete,
        onChangeRole,
        applyChanges,
        clearStaged,
        pendingCount,
    } = useStagedUserChanges(loadCptyUsers, (form, reason) =>
        dispatch(AdminActions.CreateCptyUser(form, reason)),
    );

    return (
        <div className={`${styles.userManagement} ${pendingCount > 0 ? styles.hasStatusBar : ""}`}>
            <div className={`${styles.header} ${styles.headerBack}`}>
                <button
                    className={styles.detailBtn}
                    data-testid="cpty-users-back"
                    onClick={() => history.push("/users")}
                >
                    Back to users
                </button>
                <div className={styles.title}>{cptyCode} users</div>
                <Button
                    value={showCreate ? "Cancel" : "Add User"}
                    color="green"
                    dataTestId="add-cpty-user-btn"
                    onClick={toggleCreate}
                />
            </div>

            {showCreate && createForm && (
                <div className={styles.createForm} data-testid="create-cpty-user-form">
                    <div className={styles.sectionTitle}>New {cptyCode} user</div>
                    <p className={styles.hint}>
                        The address must sit inside this counterparty's permitted email
                        domains{allowedDomains.length > 0 ? ` (${allowedDomains.join(", ")})` : ""},
                        and its UI API must be enabled. The user is added to the
                        pending changes below and created when you confirm them.
                    </p>
                    <div className={styles.formRow}>
                        <label>Email
                            <input
                                type="email"
                                data-testid="cpty-user-email"
                                value={createForm.email}
                                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                            />
                        </label>
                        <label>Role
                            <select
                                data-testid="cpty-user-role"
                                value={createForm.role}
                                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                            >
                                {CPTY_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                        </label>
                    </div>
                    <div className={styles.formRow}>
                        <label>First name
                            <input
                                type="text"
                                data-testid="cpty-user-firstname"
                                value={createForm.firstName}
                                onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                            />
                        </label>
                        <label>Last name
                            <input
                                type="text"
                                data-testid="cpty-user-lastname"
                                value={createForm.lastName}
                                onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                            />
                        </label>
                    </div>
                    <div className={styles.formRow}>
                        <div className={styles.passwordField}>
                            <span className={styles.passwordLabel}>Initial password</span>
                            <div className={styles.generatedPassword}>
                                <input
                                    type="text"
                                    className={styles.passwordValue}
                                    data-testid="cpty-user-password"
                                    value={createForm.password}
                                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                                />
                                <Button
                                    value="Copy"
                                    dataTestId="copy-cpty-user-password"
                                    onClick={() => navigator.clipboard?.writeText(createForm.password)}
                                />
                                <Button
                                    value="Regenerate"
                                    dataTestId="regen-cpty-user-password"
                                    onClick={() => setCreateForm((f) => ({ ...f, password: generatePassword() }))}
                                />
                            </div>
                        </div>
                    </div>
                    <div className={`${styles.formRow} ${styles.checkboxRow}`}>
                        <label>
                            <input
                                type="checkbox"
                                data-testid="cpty-user-email-verified"
                                checked={createForm.emailVerified}
                                onChange={(e) => setCreateForm({ ...createForm, emailVerified: e.target.checked })}
                            />
                            {" "}Mark the address verified, so the user can sign in straight away
                        </label>
                    </div>
                    {createError && <div className={styles.inlineError}>{createError}</div>}
                    <Button
                        value="Create User"
                        color="green"
                        dataTestId="submit-cpty-user"
                        onClick={submitCreate}
                    />
                </div>
            )}

            {error && <div className={styles.error}>{error}</div>}
            {resetSentTo && (
                <div className={styles.statusBar} data-testid="reset-sent">
                    Password-reset email sent to {resetSentTo}
                </div>
            )}
            {totpClearedFor && (
                <div className={styles.statusBar} data-testid="totp-cleared">
                    TOTP cleared for {totpClearedFor}. They enrol a new authenticator at their next sign-in.
                </div>
            )}

            {totpUser && (
                <div className={styles.section} data-testid="reset-totp-panel">
                    <div className={styles.sectionTitle}>Reset TOTP — {totpUser.email}</div>
                    <div className={styles.configForm}>
                        {totpError && <div className={styles.inlineError}>{totpError}</div>}
                        <p className={styles.hint}>
                            Clears the authenticator enrolled for this user only. They keep their
                            password and are prompted to enrol a new authenticator at their next
                            sign-in. Other users of {cptyCode} are unaffected.
                        </p>
                        <div className={styles.configRow}>
                            <Button
                                value="Cancel"
                                dataTestId="cancel-reset-totp"
                                onClick={() => setTotpUser(null)}
                            />
                            <Button
                                value={totpBusy ? "Clearing…" : "Clear TOTP"}
                                color="green"
                                disabled={totpBusy}
                                dataTestId="confirm-reset-totp"
                                onClick={confirmResetTotp}
                            />
                        </div>
                    </div>
                </div>
            )}

            {resetUser && (
                <div className={styles.section} data-testid="reset-panel">
                    <div className={styles.sectionTitle}>Send password reset — {resetUser.email}</div>
                    <div className={styles.configForm}>
                        {resetError && <div className={styles.inlineError}>{resetError}</div>}
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}>Subject</span>
                            <input
                                className={styles.configInput}
                                type="text"
                                value={resetSubject}
                                onChange={(e) => setResetSubject(e.target.value)}
                                data-testid="reset-subject"
                            />
                        </div>
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}>Body (use {"{{link}}"} where the reset link should appear)</span>
                            <textarea
                                className={styles.configTextarea}
                                rows={12}
                                value={resetBody}
                                onChange={(e) => setResetBody(e.target.value)}
                                data-testid="reset-body"
                            />
                        </div>
                        <div className={styles.configRow}>
                            <span className={styles.configLabel}>Link valid for</span>
                            <div className={styles.ttlControl}>
                                <input
                                    className={styles.ttlInput}
                                    type="number"
                                    min={1}
                                    value={resetTtlValue}
                                    onChange={(e) => setResetTtlValue(parseInt(e.target.value, 10) || 0)}
                                    data-testid="reset-ttl"
                                />
                                <select
                                    className={styles.ttlUnit}
                                    value={resetTtlUnit}
                                    onChange={(e) => setResetTtlUnit(e.target.value)}
                                    data-testid="reset-ttl-unit"
                                >
                                    <option value="minutes">minutes</option>
                                    <option value="hours">hours</option>
                                    <option value="days">days</option>
                                </select>
                            </div>
                        </div>
                        <div className={styles.resetActions}>
                            <Button
                                value="Cancel"
                                classes={[styles.detailBtn]}
                                onClick={() => setResetUser(null)}
                            />
                            <Button
                                value={resetBusy ? "Sending…" : "Send reset email"}
                                disabled={resetBusy || !resetSubject.trim() || !resetBody.trim() || resetTtlValue < 1}
                                onClick={sendReset}
                                dataTestId="reset-send"
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.section}>
                {loading ? (
                    <div className={styles.loading}>Loading users…</div>
                ) : (
                    <table className={styles.table} data-testid={`cpty-users-table-${cptyCode}`}>
                        <thead>
                            <tr>
                                <th>Email</th><th>Name</th><th>Role</th><th>Status</th><th>2FA</th><th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingCreates.map((c) => (
                                <PendingCreateRow
                                    key={c.stagedKey}
                                    user={c}
                                    onRemove={onUnstageCreate}
                                />
                            ))}
                            {cptyUsers.length === 0 && pendingCreates.length === 0 ? (
                                <tr><td colSpan={6} className={styles.empty}>No users for this counterparty</td></tr>
                            ) : cptyUsers.map((u) => (
                                <UserRow
                                    key={u.realm + u.id}
                                    user={u}
                                    onToggleEnabled={onToggleEnabled}
                                    onDelete={onDelete}
                                    onChangeRole={onChangeRole}
                                    onResetPassword={openReset}
                                    onResetTotp={(usr) => {
                                        setTotpClearedFor(null);
                                        setTotpError(null);
                                        setTotpUser(usr);
                                    }}
                                    onShowHistory={(usr) =>
                                        history.push({
                                            pathname: `/users/${cptyCode}/${usr.id}/history`,
                                            state: { user: usr },
                                        })
                                    }
                                    stagedRole={pendingRoles.get(u.id)?.role}
                                    pendingAction={pendingActions.get(u.id)?.kind}
                                />
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {pendingCount > 0 && (
                <div className={styles.statusBar}>
                    <span>{pendingCount} change{pendingCount > 1 ? "s" : ""} pending</span>
                    <Button
                        value="Discard Changes"
                        dataTestId="discard-user-changes"
                        classes={[styles.detailBtn]}
                        onClick={clearStaged}
                    />
                    <Button
                        value="Confirm Changes"
                        dataTestId="apply-user-changes"
                        classes={[styles.removeBtn]}
                        onClick={applyChanges}
                    />
                </div>
            )}
        </div>
    );
};

export default CptyUserManagement;
